import type { ExerciseKeeper } from "./lianxi-keeper.js";
import { type ExerciseCharacter, ExerciseRound } from "./lianxi-round.js";
import type { CharacterMarker } from "./xiezi-check.js";
import { XieziDrill } from "./xiezi-drill.js";
import type { DrillProgress } from "../handwriting/xiezi-stages.js";
import type { DrillParts } from "./xiezi-view.js";
import type { DrillSounds } from "./xiezi-sounds.js";

export type { ExerciseCharacter } from "./lianxi-round.js";

/**
 * A writing drill Exercise 练习: several characters through the four stages,
 * one stage per character at a time, in one square.
 *
 * `/lianxi/xiezi/<list>/<n>/` is a drill over a few characters of one List
 * (#961, #962), and `partials/lianxi/drill.html` draws its block. The drill on
 * a 词典 entry takes one character through its stages on its own, and this
 * holds several and each one's place, and moves the single-character drill
 * between them. `lianxi-round.ts` is the round over the characters, and this
 * is the drill and the round joined up for the block's controls.
 *
 * **The page keeps nothing, and a keeper may.** On the public page a reader
 * who leaves starts again. On `/user/lianxi/`, the handler's page for the same
 * drill, the block starts each character at the place the handler kept, and
 * an {@link ExerciseKeeper} is told each pass so the handler can keep it. The
 * keeper answers with the places as kept, which the round adopts, so two tabs
 * on one drill settle on what the handler holds. `lianxi-recorder.ts` is the
 * keeper, and the marker it also supplies posts the free stage's ink to be
 * marked there.
 */

/** What the Exercise is built from. */
export interface XieziExerciseProperties {
  readonly parts: DrillParts;

  /** The row over the square, one item per character, in the same order. */
  readonly items: readonly HTMLElement[];

  /** The characters, in the order the List's words first write them. */
  readonly characters: readonly ExerciseCharacter[];

  /** What marks a character written from memory. */
  readonly marker: CharacterMarker;

  /** Where each character starts, in the same order. The beginning where absent. */
  readonly places?: readonly DrillProgress[] | undefined;

  /** What keeps the reader's place, where anything does. */
  readonly keeper?: ExerciseKeeper | undefined;

  /** What the drill plays. Silence where absent. */
  readonly sounds?: DrillSounds | undefined;

  /** Called where the round stops a visitor at the taste. Absent for a reader with an account. */
  readonly onTasted?: (() => void) | undefined;
}

/** One Exercise, on one block, over its characters. */
export class XieziExercise {
  readonly #drill: XieziDrill;
  readonly #round: ExerciseRound;

  constructor(properties: XieziExerciseProperties) {
    const { parts, marker, characters } = properties;
    const [first] = characters;
    properties.sounds?.preload?.(
      characters.flatMap((held) => held.recordings ?? []),
    );

    if (first === undefined) {
      throw new Error("An Exercise needs at least one character.");
    }

    // The round puts characters in the drill and the drill tells the round
    // when a pass ends, so each is handed somewhere the other will be.
    const holder: { drill?: XieziDrill } = {};

    this.#round = new ExerciseRound({
      ...properties,
      show: (held): void => {
        holder.drill?.show(held);
      },
    });
    this.#drill = new XieziDrill({
      parts,
      ...first,
      marker,
      sounds: properties.sounds,
      onPass: (progress): void => {
        this.#round.passEnded(progress);
      },
      finishes: (): boolean => this.#round.othersFinished(),
    });
    holder.drill = this.#drill;
    this.#round.start();
  }

  /** A stroke has landed on the pad. */
  strokeLanded(): void {
    if (!this.#round.waiting) {
      this.#drill.strokeLanded();
    }
  }

  /** The last stroke taken back. */
  undo(): void {
    if (!this.#round.waiting) {
      this.#drill.undo();
    }
  }

  /** The pass started again. */
  clear(): void {
    if (!this.#round.waiting) {
      this.#drill.clear();
    }
  }

  /** The character written from memory, marked whole. */
  async check(): Promise<void> {
    if (!this.#round.waiting) {
      await this.#drill.check();
    }
  }

  /** The Exercise started again from the first character's first stage. */
  again(): void {
    this.#round.again();
  }
}
